// SPDX-License-Identifier: AGPL-3.0-or-later

import { create } from 'zustand';
import { ExtensionUrl, type Questionnaire, type QuestionnaireItem } from '@intakeform/fhir';
import { newLinkId, type PaletteEntry } from '@intakeform/ui';

export interface BuilderState {
  draft: Questionnaire | undefined;
  /** Routable form classification (app metadata, not part of the FHIR
   *  resource). Drives external-API filtering + webhook routing. */
  formType: string | null;
  selectedLinkId: string | undefined;
  collapsedSections: Record<string, boolean>;
  load: (questionnaire: Questionnaire, formType?: string | null) => void;
  reset: () => void;
  setTitle: (title: string) => void;
  setFormType: (formType: string | null) => void;
  /** Set the form-level magic-link TTL in seconds (xv2). undefined removes the extension. */
  setLinkTtlSeconds: (seconds: number | undefined) => void;
  addFromPalette: (entry: PaletteEntry) => void;
  addToSection: (sectionLinkId: string, entry: PaletteEntry) => void;
  addFromPaletteRelative: (
    entry: PaletteEntry,
    targetLinkId: string,
    position: 'before' | 'after' | 'inside',
  ) => void;
  updateItem: (next: QuestionnaireItem) => void;
  moveItem: (linkId: string, direction: -1 | 1) => void;
  moveItemRelative: (
    activeId: string,
    overId: string,
    position: 'before' | 'after' | 'inside',
  ) => void;
  removeItem: (linkId: string) => void;
  select: (linkId: string | undefined) => void;
  toggleSectionCollapse: (linkId: string) => void;
}

function blank(): Questionnaire {
  return {
    resourceType: 'Questionnaire',
    status: 'draft',
    title: 'Untitled form',
    item: [],
  } satisfies Questionnaire;
}

function updateItemInItems(
  items: QuestionnaireItem[],
  linkId: string,
  replacement: QuestionnaireItem,
): QuestionnaireItem[] {
  return items.map((it) => {
    if (it.linkId === linkId) {
      return replacement;
    }
    if (it.type === 'group' && it.item) {
      return {
        ...it,
        item: updateItemInItems(it.item, linkId, replacement),
      };
    }
    return it;
  });
}

function removeItemFromItems(items: QuestionnaireItem[], linkId: string): QuestionnaireItem[] {
  return items
    .filter((it) => it.linkId !== linkId)
    .map((it) => {
      if (it.type === 'group' && it.item) {
        return {
          ...it,
          item: removeItemFromItems(it.item, linkId),
        };
      }
      return it;
    });
}

function moveInArray<T>(arr: readonly T[], idx: number, dir: -1 | 1): T[] {
  const next = [...arr];
  const target = idx + dir;
  if (idx < 0 || target < 0 || target >= next.length) return next;
  const a = next[idx];
  const b = next[target];
  if (a === undefined || b === undefined) return next;
  next[idx] = b;
  next[target] = a;
  return next;
}

function moveItemInItems(
  items: QuestionnaireItem[],
  linkId: string,
  direction: -1 | 1,
): { items: QuestionnaireItem[]; moved: boolean } {
  const idx = items.findIndex((it) => it.linkId === linkId);
  if (idx !== -1) {
    return {
      items: moveInArray(items, idx, direction),
      moved: true,
    };
  }

  let moved = false;
  const nextItems = items.map((it) => {
    if (it.type === 'group' && it.item) {
      const res = moveItemInItems(it.item, linkId, direction);
      if (res.moved) {
        moved = true;
        return { ...it, item: res.items };
      }
    }
    return it;
  });

  return { items: nextItems, moved };
}

function addItemToSection(
  items: QuestionnaireItem[],
  sectionLinkId: string,
  newItem: QuestionnaireItem,
): QuestionnaireItem[] {
  return items.map((it) => {
    if (it.linkId === sectionLinkId && it.type === 'group') {
      return {
        ...it,
        item: [...(it.item ?? []), newItem],
      };
    }
    if (it.type === 'group' && it.item) {
      return {
        ...it,
        item: addItemToSection(it.item, sectionLinkId, newItem),
      };
    }
    return it;
  });
}

function pluckItem(
  items: QuestionnaireItem[],
  linkId: string,
): { items: QuestionnaireItem[]; plucked: QuestionnaireItem | undefined } {
  let plucked: QuestionnaireItem | undefined = undefined;

  const filterAndPluck = (list: QuestionnaireItem[]): QuestionnaireItem[] => {
    const idx = list.findIndex((item) => item.linkId === linkId);
    if (idx !== -1) {
      plucked = list[idx];
      return list.filter((_, i) => i !== idx);
    }
    return list.map((item) => {
      if (item.type === 'group' && item.item) {
        return { ...item, item: filterAndPluck(item.item) };
      }
      return item;
    });
  };

  const nextItems = filterAndPluck(items);
  return { items: nextItems, plucked };
}

function insertItemRelative(
  items: QuestionnaireItem[],
  targetLinkId: string,
  itemToInsert: QuestionnaireItem,
  position: 'before' | 'after' | 'inside',
): QuestionnaireItem[] {
  if (position === 'inside') {
    return items.map((it) => {
      if (it.linkId === targetLinkId && it.type === 'group') {
        return {
          ...it,
          item: [...(it.item ?? []), itemToInsert],
        };
      }
      if (it.type === 'group' && it.item) {
        return { ...it, item: insertItemRelative(it.item, targetLinkId, itemToInsert, position) };
      }
      return it;
    });
  }

  const idx = items.findIndex((it) => it.linkId === targetLinkId);
  if (idx !== -1) {
    const next = [...items];
    const insertIdx = position === 'before' ? idx : idx + 1;
    next.splice(insertIdx, 0, itemToInsert);
    return next;
  }

  return items.map((it) => {
    if (it.type === 'group' && it.item) {
      return {
        ...it,
        item: insertItemRelative(it.item, targetLinkId, itemToInsert, position),
      };
    }
    return it;
  });
}

export const useBuilderStore = create<BuilderState>((set) => ({
  draft: undefined,
  formType: null,
  selectedLinkId: undefined,
  collapsedSections: {},
  load: (questionnaire, formType = null) =>
    set({ draft: questionnaire, formType, selectedLinkId: undefined, collapsedSections: {} }),
  reset: () =>
    set({ draft: blank(), formType: null, selectedLinkId: undefined, collapsedSections: {} }),
  setTitle: (title) => set((s) => (s.draft === undefined ? s : { draft: { ...s.draft, title } })),
  setFormType: (formType) => set({ formType }),
  setLinkTtlSeconds: (seconds) =>
    set((s) => {
      if (s.draft === undefined) return s;
      // Strip any prior link-ttl-seconds extension; replace with the
      // new value when defined. Empty/undefined → removed entirely.
      const stripped = (s.draft.extension ?? []).filter(
        (e) => e.url !== ExtensionUrl.linkTtlSeconds,
      );
      const next =
        seconds === undefined
          ? stripped
          : [...stripped, { url: ExtensionUrl.linkTtlSeconds, valueInteger: seconds }];
      return {
        draft: {
          ...s.draft,
          extension: next.length > 0 ? next : undefined,
        },
      };
    }),
  addFromPalette: (entry) =>
    set((s) => {
      const draft = s.draft ?? blank();
      const template = entry.template();
      const item: QuestionnaireItem = {
        ...template,
        linkId: template.linkId ?? newLinkId(template.type ?? 'field'),
      };
      return {
        draft: { ...draft, item: [...(draft.item ?? []), item] },
        selectedLinkId: item.linkId,
      };
    }),
  addToSection: (sectionLinkId, entry) =>
    set((s) => {
      if (s.draft === undefined) return s;
      const draft = s.draft;
      const template = entry.template();
      const item: QuestionnaireItem = {
        ...template,
        linkId: template.linkId ?? newLinkId(template.type ?? 'field'),
      };
      return {
        draft: {
          ...draft,
          item: addItemToSection(draft.item ?? [], sectionLinkId, item),
        },
        selectedLinkId: item.linkId,
      };
    }),
  addFromPaletteRelative: (entry, targetLinkId, position) =>
    set((s) => {
      if (s.draft === undefined) return s;
      const draft = s.draft;
      const template = entry.template();
      const item: QuestionnaireItem = {
        ...template,
        linkId: template.linkId ?? newLinkId(template.type ?? 'field'),
      };
      const finalItems = insertItemRelative(draft.item ?? [], targetLinkId, item, position);
      return {
        draft: { ...draft, item: finalItems },
        selectedLinkId: item.linkId,
      };
    }),
  updateItem: (next) =>
    set((s) => {
      if (s.draft === undefined) return s;
      return {
        draft: {
          ...s.draft,
          item: updateItemInItems(s.draft.item ?? [], next.linkId, next),
        },
      };
    }),
  moveItem: (linkId, direction) =>
    set((s) => {
      if (s.draft === undefined) return s;
      const res = moveItemInItems(s.draft.item ?? [], linkId, direction);
      if (!res.moved) return s;
      return { draft: { ...s.draft, item: res.items } };
    }),
  moveItemRelative: (activeId, overId, position) =>
    set((s) => {
      if (s.draft === undefined) return s;
      const { items: pluckedItems, plucked } = pluckItem(s.draft.item ?? [], activeId);
      if (!plucked) return s;
      const finalItems = insertItemRelative(pluckedItems, overId, plucked, position);
      return {
        draft: {
          ...s.draft,
          item: finalItems,
        },
      };
    }),
  removeItem: (linkId) =>
    set((s) => {
      if (s.draft === undefined) return s;
      return {
        draft: {
          ...s.draft,
          item: removeItemFromItems(s.draft.item ?? [], linkId),
        },
        selectedLinkId: s.selectedLinkId === linkId ? undefined : s.selectedLinkId,
      };
    }),
  select: (linkId) => set({ selectedLinkId: linkId }),
  toggleSectionCollapse: (linkId) =>
    set((s) => ({
      collapsedSections: {
        ...s.collapsedSections,
        [linkId]: !s.collapsedSections[linkId],
      },
    })),
}));
